// CaptureFab — the bottom-right "New" capture button for a farm list page
// (mobile-first plan WS-10): the page's most-used capture action reachable
// one-handed on a phone. Touch-only — pointer devices keep using the header
// button; "touch" is useDeviceClass() !== 'desktop', the same answer the
// pickers use (farm audit X-11), and the same `(pointer: coarse)` test as a
// page's `coarse:hidden` header button, so exactly one of the two shows — and
// PAGE-scoped: this is NOT the global palette FAB App.tsx
// removed ("it stole the phone thumb zone" — that one floated on every route;
// this renders only on a capture list and does that list's one thing).
//
// Coexistence (mobile-audit S4 family): `.above-bottom-nav-gap` (index.css)
// lifts it clear of the bottom tab bar; a `has-capture-fab` body class lets
// the offline-queue badge vacate the right-4 slot (compound variant in
// OfflineQueueBadge.tsx); and it stands down entirely while the update
// banner needs the corner.

import { useEffect } from 'react'
import { Plus } from 'lucide-react'
import { useDeviceClass } from '@/hooks/useDeviceClass'

export default function CaptureFab({ label, onClick }: {
  /** Action label, e.g. "New pick" — rendered beside the +, and the aria-label. */
  label: string
  onClick: () => void
}) {
  const touch = useDeviceClass() !== 'desktop'
  // The body class moves the offline badge out of the corner — only while the
  // button is actually there to need it.
  useEffect(() => {
    if (!touch) return
    document.body.classList.add('has-capture-fab')
    return () => document.body.classList.remove('has-capture-fab')
  }, [touch])
  if (!touch) return null
  // short: on a phone turned sideways the labelled pill (137×56) sat on top of
  // Scouting's filter row (farm audit X-02) — it becomes a 56px round "+".
  // aria-label already carries the label, so nothing is lost to a screen reader.
  return (
    <button type="button" onClick={onClick} aria-label={label} data-no-press
      className="flex [body.update-banner-visible_&]:!hidden print:hidden
        fixed right-4 bottom-4 above-bottom-nav-gap z-chrome
        h-14 px-5 short:w-14 short:px-0 items-center justify-center gap-1.5 rounded-full
        bg-green-600 hover:bg-green-500 active:bg-green-700 text-white font-semibold
        shadow-lg shadow-black/40">
      <Plus size={20} /> <span className="text-sm short:hidden">{label}</span>
    </button>
  )
}
